import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { settingsService } from '@leoclinic/services';
import type { ClinicSettings } from '@leoclinic/types';

const SETTINGS_KEY = ['settings'] as const;

export function useSettings(clinicId: string | null) {
  return useQuery<ClinicSettings>({
    queryKey: [...SETTINGS_KEY, clinicId],
    queryFn: () => settingsService.getAll(clinicId!),
    enabled: !!clinicId,
  });
}

export function useSettingsMutations(clinicId: string | null) {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });

  const upsertMutation = useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) =>
      settingsService.upsert(clinicId!, key, value),
    onSuccess: invalidate,
  });

  const uploadLogoMutation = useMutation({
    mutationFn: (file: File) => settingsService.uploadAppLogo(clinicId!, file),
    onSuccess: invalidate,
  });

  const clearLogoMutation = useMutation({
    mutationFn: () => settingsService.clearAppLogo(clinicId!),
    onSuccess: invalidate,
  });

  return {
    mutateAsync: upsertMutation.mutateAsync,
    uploadAppLogo: uploadLogoMutation.mutateAsync,
    clearAppLogo: clearLogoMutation.mutateAsync,
    isPending: upsertMutation.isPending,
    isUploadingLogo: uploadLogoMutation.isPending,
    isClearingLogo: clearLogoMutation.isPending,
  };
}

export { SETTINGS_KEY };
